// eslint-disable-next-line import/no-unresolved
import { CloudAppSelection, CloudStreamOptions } from '@zerolight-core/libzl/types/src/globals';

import {
	createContext,
	useContext,
	useState,
	useEffect,
	useCallback,
	PropsWithChildren,
} from 'react';

import { authenticate, checkForExistingToken } from './utils/authenticate';

export type OptionalStreamOpts = {
	cloudConnectionParameters: { customer: string; renderService: string; region?: string };
	appSelect?: CloudAppSelection;
	consumeMouseClicks?: boolean;
	fakeMouseWithTouch?: boolean;
	idleDisconnectTime?: number;
	portalCAS?: boolean;
};

interface ICloudStreamOptionsContext {
	options?: CloudStreamOptions;
	ready: boolean;
	updateStreamOptions: (options: OptionalStreamOpts) => void;
}

export const CloudStreamOptionsContext = createContext<ICloudStreamOptionsContext>({
	options: undefined,
	ready: false,
	updateStreamOptions: () => null,
});
export const useCloudStreamOptions = () => useContext(CloudStreamOptionsContext);

export const defaultIdleTimeout = 1000 * 60 * 15;

export const CloudStreamOptionsProvider: React.FC<PropsWithChildren> = ({ children }) => {
	const [token, setToken] = useState<string | undefined>();
	const [connectionOptions, setConnectionOptions] = useState<CloudStreamOptions>({
		cloudConnectionParameters: {
			customer: '',
			renderService: '',
			environment: '',
			region: '',
		},
		streamingMode: 'video',
		parent: 'streamContainer',
		directConnection: false,
		consumeMouseClicks: false,
		fakeMouseWithTouch: false,
		idleDisconnectTime: defaultIdleTimeout,
	});

	const updateStreamOptions = useCallback(
		(options: OptionalStreamOpts) => {
			const casURL =
				options.portalCAS && options?.cloudConnectionParameters?.region
					? `/api/v0/cas/${options?.cloudConnectionParameters.customer}/${options?.cloudConnectionParameters.renderService}/${options?.cloudConnectionParameters.region}`
					: undefined;
			setConnectionOptions({
				...connectionOptions,
				cloudConnectionParameters: {
					...connectionOptions.cloudConnectionParameters,
					...options.cloudConnectionParameters,
				},
				appSelect: options.appSelect || undefined,
				consumeMouseClicks: options.consumeMouseClicks || false,
				fakeMouseWithTouch: options.fakeMouseWithTouch || false,
				idleDisconnectTime: options.idleDisconnectTime || defaultIdleTimeout,
				token,
				casURL,
			});
		},
		[connectionOptions, token],
	);

	const tokenRequired = window.location.search.includes('auth=');

	const requestToken = useCallback(async () => {
		if (tokenRequired) {
			if (connectionOptions.cloudConnectionParameters.customer) {
				const existingToken = await checkForExistingToken(
					connectionOptions.cloudConnectionParameters.customer,
					connectionOptions.cloudConnectionParameters.renderService,
				);

				if (existingToken) {
					if (token !== existingToken) {
						setToken(existingToken);
						setConnectionOptions({ ...connectionOptions, token: existingToken });
					}
				} else {
					const newToken = await authenticate(
						connectionOptions.cloudConnectionParameters.customer,
						connectionOptions.cloudConnectionParameters.renderService,
					);
					if (newToken) {
						setToken(newToken || '');
					}
				}
			} else {
				if (token) {
					setToken('');
				}
			}
		}
	}, [tokenRequired, connectionOptions, token, setToken]);

	useEffect(() => {
		requestToken();
	}, [requestToken]);

	const ready = !!(
		(!tokenRequired || token) &&
		connectionOptions.cloudConnectionParameters.renderService
	);

	return (
		<CloudStreamOptionsContext.Provider
			value={{ options: connectionOptions, ready, updateStreamOptions }}
		>
			{children}
		</CloudStreamOptionsContext.Provider>
	);
};
